<template>
    <section class="student-profile">
        <div class="container student-profile__container">
            <StudentSidebar :student="student" />

            <main class="student-profile__main">
                <header class="student-profile__topbar">
                    <div>
                        <span class="student-profile__eyebrow">Profile</span>
                        <h1>Edit Profile</h1>
                        <p>Keep your student account details accurate and ready for course activity.</p>
                    </div>
                    <a href="/student/dashboard" class="student-profile__back">
                        <i class="ri-arrow-left-line"></i>
                        Dashboard
                    </a>
                </header>

                <section class="student-profile__workspace">
                    <div class="student-profile__editor">
                        <form class="student-profile__stack" @submit.prevent="submitProfile">
                            <section class="student-profile__panel student-profile__photo-panel">
                                <div class="student-profile__avatar-uploader">
                                    <img :src="profilePreview" alt="Profile image" />
                                    <label for="student-profile-image" aria-label="Change profile image">
                                        <i class="ri-camera-line"></i>
                                    </label>
                                </div>

                                <input
                                    id="student-profile-image"
                                    type="file"
                                    accept="image/*"
                                    class="d-none"
                                    @change="handleProfileImage"
                                />

                                <div class="student-profile__photo-copy">
                                    <span>Update Your Profile Photo</span>
                                    <strong>{{ fullName }}</strong>
                                    <small>At least 800x800 px recommended. JPG, PNG or WEBP is allowed.</small>
                                    <p v-if="profileErrors.profile_image" class="student-profile__error">
                                        {{ profileErrors.profile_image[0] }}
                                    </p>
                                </div>
                            </section>

                            <section class="student-profile__panel">
                                <div class="student-profile__panel-head">
                                    <div>
                                        <span class="student-profile__eyebrow">Personal info</span>
                                        <h2>Basic Details</h2>
                                    </div>
                                    <button type="submit" :disabled="profileLoading" class="student-profile__save-inline">
                                        <i class="ri-save-3-line"></i>
                                        {{ profileLoading ? 'Updating...' : 'Update' }}
                                    </button>
                                </div>

                                <div class="student-profile__fields">
                                    <div class="student-profile__field">
                                        <label for="student-name">First Name</label>
                                        <input id="student-name" v-model="profileForm.name" type="text" />
                                        <p v-if="profileErrors.name">{{ profileErrors.name[0] }}</p>
                                    </div>

                                    <div class="student-profile__field">
                                        <label for="student-last-name">Last Name</label>
                                        <input id="student-last-name" v-model="profileForm.last_name" type="text" />
                                        <p v-if="profileErrors.last_name">{{ profileErrors.last_name[0] }}</p>
                                    </div>

                                    <div class="student-profile__field">
                                        <label for="student-email">Email</label>
                                        <input id="student-email" v-model="profileForm.email" type="email" />
                                        <p v-if="profileErrors.email">{{ profileErrors.email[0] }}</p>
                                    </div>

                                    <div class="student-profile__field">
                                        <label for="student-phone">Phone</label>
                                        <input id="student-phone" v-model="profileForm.phone" type="text" />
                                        <p v-if="profileErrors.phone">{{ profileErrors.phone[0] }}</p>
                                    </div>

                                    <div class="student-profile__field student-profile__field--full">
                                        <label for="student-company">Company / Institution</label>
                                        <input id="student-company" v-model="profileForm.company_name" type="text" />
                                        <p v-if="profileErrors.company_name">{{ profileErrors.company_name[0] }}</p>
                                    </div>
                                </div>
                            </section>
                        </form>

                        <form class="student-profile__panel" @submit.prevent="submitPassword">
                            <div class="student-profile__panel-head">
                                <div>
                                    <span class="student-profile__eyebrow">Secure</span>
                                    <h2>Password</h2>
                                </div>
                            </div>

                            <div class="student-profile__password-fields">
                                <div class="student-profile__field">
                                    <label for="student-password">New Password</label>
                                    <div class="student-profile__password-input">
                                        <input
                                            id="student-password"
                                            v-model="passwordForm.password"
                                            :type="showPassword ? 'text' : 'password'"
                                            placeholder="Enter new password"
                                        />
                                        <button type="button" :aria-label="showPassword ? 'Hide password' : 'Show password'" @click="showPassword = !showPassword">
                                            <i :class="showPassword ? 'ri-eye-off-line' : 'ri-eye-line'"></i>
                                        </button>
                                    </div>
                                    <p v-if="passwordErrors.password">{{ passwordErrors.password[0] }}</p>
                                </div>

                                <div class="student-profile__field">
                                    <label for="student-password-confirmation">Confirm Password</label>
                                    <div class="student-profile__password-input">
                                        <input
                                            id="student-password-confirmation"
                                            v-model="passwordForm.password_confirmation"
                                            :type="showConfirmPassword ? 'text' : 'password'"
                                            placeholder="Confirm new password"
                                        />
                                        <button
                                            type="button"
                                            :aria-label="showConfirmPassword ? 'Hide confirm password' : 'Show confirm password'"
                                            @click="showConfirmPassword = !showConfirmPassword"
                                        >
                                            <i :class="showConfirmPassword ? 'ri-eye-off-line' : 'ri-eye-line'"></i>
                                        </button>
                                    </div>
                                    <p v-if="passwordErrors.password_confirmation">{{ passwordErrors.password_confirmation[0] }}</p>
                                </div>
                            </div>

                            <div class="student-profile__actions">
                                <button type="submit" :disabled="passwordLoading">
                                    <i class="ri-lock-password-line"></i>
                                    {{ passwordLoading ? 'Updating...' : 'Update Password' }}
                                </button>
                            </div>
                        </form>
                    </div>

                    <aside class="student-profile__panel student-profile__completion">
                        <div class="student-profile__completion-head">
                            <span class="student-profile__eyebrow">Profile strength</span>
                            <h2>Complete your profile</h2>
                        </div>

                        <div class="student-profile__completion-ring" :style="completionCircleStyle">
                            <div>
                                <strong>{{ completionScore }}%</strong>
                            </div>
                        </div>

                        <div class="student-profile__completion-list">
                            <div
                                v-for="item in completionItems"
                                :key="item.label"
                                :class="{ 'is-complete': item.done }"
                            >
                                <i :class="item.done ? 'ri-check-line' : 'ri-close-line'"></i>
                                <span>{{ item.label }}</span>
                                <small>{{ item.weight }}%</small>
                            </div>
                        </div>
                    </aside>
                </section>
            </main>
        </div>
    </section>
</template>

<script setup>
import { computed, reactive, ref } from 'vue';
import StudentSidebar from '@/components/website/student/StudentSidebar.vue';

const root = document.getElementById('studentProfileComponent');
const defaultProfileImage = '/media/default/default-image.jpg';

const parseJson = (value, fallback = {}) => {
    try {
        return value ? JSON.parse(value) : fallback;
    } catch (error) {
        return fallback;
    }
};

const student = reactive(parseJson(root?.dataset.student));
const profileUpdateUrl = root?.dataset.profileUpdateUrl;
const passwordUpdateUrl = root?.dataset.passwordUpdateUrl;

const profileForm = reactive({
    name: student.name || '',
    last_name: student.last_name || '',
    email: student.email || '',
    phone: student.phone || '',
    company_name: student.company_name || '',
    profile_image: null,
});

const passwordForm = reactive({
    password: '',
    password_confirmation: '',
});

const profilePreview = ref(student.profile_path || defaultProfileImage);
const profileErrors = ref({});
const passwordErrors = ref({});
const profileLoading = ref(false);
const passwordLoading = ref(false);
const showPassword = ref(false);
const showConfirmPassword = ref(false);

const fullName = computed(() => {
    const name = [profileForm.name, profileForm.last_name].filter(Boolean).join(' ').trim();
    return name || 'Student';
});

const hasProfilePhoto = computed(() => Boolean(student.profile_path || profileForm.profile_image));

const completionItems = computed(() => [
    {
        label: 'Setup account',
        weight: 20,
        done: Boolean(student.id || profileForm.email),
    },
    {
        label: 'Upload your photo',
        weight: 15,
        done: hasProfilePhoto.value,
    },
    {
        label: 'Personal info',
        weight: 25,
        done: Boolean(profileForm.name && profileForm.last_name && profileForm.email),
    },
    {
        label: 'Contact number',
        weight: 20,
        done: Boolean(profileForm.phone),
    },
    {
        label: 'Institution',
        weight: 20,
        done: Boolean(profileForm.company_name),
    },
]);

const completionScore = computed(() => completionItems.value.reduce((total, item) => total + (item.done ? item.weight : 0), 0));

const completionCircleStyle = computed(() => ({
    '--profile-completion': `${completionScore.value * 3.6}deg`,
}));

const notify = (message, type = 'success') => {
    if (typeof window.$toast === 'function') {
        window.$toast(message, type);
    }
};

const handleProfileImage = (event) => {
    const file = event.target.files?.[0];
    profileForm.profile_image = file || null;

    if (file) {
        profilePreview.value = URL.createObjectURL(file);
    }
};

const submitProfile = async () => {
    profileLoading.value = true;
    profileErrors.value = {};

    const formData = new FormData();
    formData.append('name', profileForm.name);
    formData.append('last_name', profileForm.last_name);
    formData.append('email', profileForm.email);
    formData.append('phone', profileForm.phone);
    formData.append('company_name', profileForm.company_name);

    if (profileForm.profile_image) {
        formData.append('profile_image', profileForm.profile_image);
    }

    try {
        const { data } = await axios.post(profileUpdateUrl, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });

        if (data.status) {
            Object.assign(student, data.data || {});
            profilePreview.value = student.profile_path || defaultProfileImage;
            profileForm.profile_image = null;
            notify(data.message || 'Profile updated successfully.', data.type || 'success');
        }
    } catch (error) {
        if (error.response?.status === 422) {
            profileErrors.value = error.response.data.errors || {};
            notify(error.response.data.message || 'Please fix validation errors.', 'error');
        } else {
            notify(error.message || 'Something went wrong.', 'error');
        }
    } finally {
        profileLoading.value = false;
    }
};

const submitPassword = async () => {
    passwordLoading.value = true;
    passwordErrors.value = {};

    try {
        const { data } = await axios.post(passwordUpdateUrl, passwordForm);

        if (data.status) {
            passwordForm.password = '';
            passwordForm.password_confirmation = '';
            showPassword.value = false;
            showConfirmPassword.value = false;
            notify(data.message || 'Password updated successfully.', data.type || 'success');
        }
    } catch (error) {
        if (error.response?.status === 422) {
            passwordErrors.value = error.response.data.errors || {};
            notify(error.response.data.message || 'Please fix validation errors.', 'error');
        } else {
            notify(error.message || 'Something went wrong.', 'error');
        }
    } finally {
        passwordLoading.value = false;
    }
};
</script>

<style scoped>
.student-profile {
    background:
        radial-gradient(circle at top left, rgba(13, 110, 253, 0.1), transparent 32%),
        linear-gradient(180deg, #f5f8fc 0%, #edf1f6 52%, #f8fafc 100%);
    padding: 32px 0 46px;
}

.student-profile__container {
    display: grid;
    grid-template-columns: 240px minmax(0, 1fr);
    gap: 24px;
    width: 100%;
    align-items: start;
}

.student-profile__main {
    min-width: 0;
}

.student-profile__topbar,
.student-profile__panel {
    background: #ffffff;
    border: 1px solid rgba(226, 232, 240, 0.92);
    border-radius: 8px;
    box-shadow: 0 18px 42px rgba(15, 23, 42, 0.06);
}

.student-profile__topbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 18px 22px;
    margin-bottom: 18px;
    background:
        linear-gradient(135deg, rgba(13, 110, 253, 0.08), rgba(32, 201, 151, 0.06)),
        #ffffff;
    border-color: #dcecff;
}

.student-profile__topbar h1,
.student-profile__panel-head h2 {
    margin: 0;
    color: #111827;
    font-weight: 700;
}

.student-profile__topbar h1 {
    font-size: 24px;
}

.student-profile__topbar p {
    max-width: 540px;
    margin: 6px 0 0;
    color: #64748b;
    font-size: 13px;
    line-height: 1.5;
}

.student-profile__eyebrow {
    display: block;
    margin-bottom: 4px;
    color: #0d6efd;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0;
    text-transform: uppercase;
}

.student-profile__back {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    min-height: 40px;
    padding: 9px 14px;
    border-radius: 8px;
    background: #e7f1ff;
    color: #0d6efd;
    font-size: 13px;
    font-weight: 800;
    text-decoration: none;
    transition: background 0.2s ease, color 0.2s ease, transform 0.2s ease;
}

.student-profile__back:hover {
    background: #dbeafe;
    color: #0b5ed7;
    transform: translateY(-1px);
}

.student-profile__workspace {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 280px;
    gap: 18px;
    align-items: start;
}

.student-profile__editor,
.student-profile__stack {
    display: grid;
    gap: 14px;
}

.student-profile__panel {
    position: relative;
    overflow: hidden;
    padding: 22px;
}

.student-profile__panel::before {
    position: absolute;
    top: 0;
    right: 0;
    left: 0;
    height: 4px;
    background: linear-gradient(90deg, #0d6efd 0%, #20c997 56%, #7c3aed 100%);
    content: "";
}

.student-profile__panel-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 14px;
    margin-bottom: 18px;
}

.student-profile__panel-head h2 {
    font-size: 19px;
}

.student-profile__photo-panel {
    display: flex;
    align-items: center;
    gap: 22px;
    min-height: 154px;
    background:
        radial-gradient(circle at left center, rgba(13, 110, 253, 0.12), transparent 36%),
        linear-gradient(180deg, #ffffff 0%, #fbfdff 100%);
}

.student-profile__avatar-uploader {
    position: relative;
    flex: 0 0 118px;
    width: 118px;
    height: 118px;
}

.student-profile__avatar-uploader img {
    width: 100%;
    height: 100%;
    border: 4px solid #ffffff;
    border-radius: 50%;
    object-fit: cover;
    box-shadow:
        0 14px 30px rgba(15, 23, 42, 0.13),
        0 0 0 8px rgba(231, 241, 255, 0.92);
}

.student-profile__avatar-uploader label {
    position: absolute;
    right: -2px;
    bottom: 9px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 38px;
    height: 38px;
    border: 3px solid #ffffff;
    border-radius: 50%;
    background: linear-gradient(135deg, #0d6efd 0%, #20c997 100%);
    color: #ffffff;
    font-size: 17px;
    box-shadow: 0 12px 24px rgba(13, 110, 253, 0.24);
    cursor: pointer;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.student-profile__avatar-uploader label:hover {
    transform: translateY(-2px);
    box-shadow: 0 16px 30px rgba(13, 110, 253, 0.3);
}

.student-profile__photo-copy {
    display: grid;
    gap: 5px;
    min-width: 0;
}

.student-profile__photo-copy span {
    color: #0d6efd;
    font-size: 12px;
    font-weight: 800;
    text-transform: uppercase;
}

.student-profile__photo-copy strong {
    color: #111827;
    font-size: 24px;
    font-weight: 800;
    text-transform: capitalize;
}

.student-profile__photo-copy small {
    max-width: 360px;
    color: #64748b;
    font-size: 13px;
    line-height: 1.5;
}

.student-profile__fields {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 14px;
}

.student-profile__password-fields {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 14px;
}

.student-profile__field {
    min-width: 0;
}

.student-profile__field--full {
    grid-column: 1 / -1;
}

.student-profile__field label {
    display: block;
    margin-bottom: 7px;
    color: #64748b;
    font-size: 12px;
    font-weight: 800;
    text-transform: uppercase;
}

.student-profile__field input {
    width: 100%;
    min-height: 46px;
    padding: 11px 13px;
    border: 1px solid #dbe4ef;
    border-radius: 8px;
    color: #111827;
    font-size: 14px;
    outline: none;
    background: #ffffff;
    transition: border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
}

.student-profile__field input:focus {
    background: #fbfdff;
    border-color: #0d6efd;
    box-shadow: 0 0 0 3px rgba(13, 110, 253, 0.13);
}

.student-profile__field p,
.student-profile__error {
    margin: 6px 0 0;
    color: #dc2626;
    font-size: 12px;
    font-weight: 700;
}

.student-profile__password-input {
    position: relative;
}

.student-profile__password-input input {
    padding-right: 44px;
}

.student-profile__password-input button {
    position: absolute;
    top: 50%;
    right: 8px;
    display: grid;
    place-items: center;
    width: 32px;
    height: 32px;
    border: 0;
    border-radius: 8px;
    background: #e7f1ff;
    color: #0d6efd;
    transform: translateY(-50%);
    transition: background 0.2s ease, color 0.2s ease;
}

.student-profile__password-input button:hover {
    background: #dbeafe;
    color: #0b5ed7;
}

.student-profile__actions {
    display: flex;
    justify-content: flex-end;
    margin-top: 20px;
}

.student-profile__actions button,
.student-profile__save-inline {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    min-height: 44px;
    padding: 10px 16px;
    border: 0;
    border-radius: 8px;
    background: linear-gradient(135deg, #0d6efd 0%, #2f7d5a 100%);
    color: #ffffff;
    font-size: 14px;
    font-weight: 800;
    box-shadow: 0 14px 28px rgba(13, 110, 253, 0.18);
    transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.student-profile__save-inline {
    min-height: 38px;
    padding: 8px 13px;
    font-size: 13px;
}

.student-profile__actions button:not(:disabled):hover,
.student-profile__save-inline:not(:disabled):hover {
    transform: translateY(-2px);
    box-shadow: 0 18px 34px rgba(13, 110, 253, 0.24);
}

.student-profile__actions button:disabled,
.student-profile__save-inline:disabled {
    cursor: not-allowed;
    opacity: 0.72;
}

.student-profile__completion {
    position: sticky;
    top: 24px;
    display: grid;
    justify-items: center;
    gap: 18px;
    padding: 22px 20px;
}

.student-profile__completion-head {
    width: 100%;
}

.student-profile__completion-head h2 {
    margin: 0;
    color: #111827;
    font-size: 17px;
    font-weight: 800;
}

.student-profile__completion-ring {
    display: grid;
    place-items: center;
    width: 136px;
    height: 136px;
    border-radius: 50%;
    background: conic-gradient(#20c997 0 var(--profile-completion), #e2e8f0 var(--profile-completion) 360deg);
    box-shadow: inset 0 0 0 1px rgba(148, 163, 184, 0.12);
}

.student-profile__completion-ring > div {
    display: grid;
    place-items: center;
    width: 92px;
    height: 92px;
    border-radius: 50%;
    background: #ffffff;
    box-shadow: 0 10px 24px rgba(15, 23, 42, 0.08);
}

.student-profile__completion-ring strong {
    color: #111827;
    font-size: 24px;
    font-weight: 900;
}

.student-profile__completion-list {
    display: grid;
    gap: 12px;
    width: 100%;
}

.student-profile__completion-list > div {
    display: grid;
    grid-template-columns: 20px minmax(0, 1fr) auto;
    align-items: center;
    gap: 8px;
    color: #64748b;
    font-size: 13px;
    font-weight: 700;
}

.student-profile__completion-list i {
    display: grid;
    place-items: center;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: #f1f5f9;
    color: #94a3b8;
    font-size: 14px;
}

.student-profile__completion-list small {
    color: #94a3b8;
    font-size: 12px;
    font-weight: 800;
}

.student-profile__completion-list > div.is-complete {
    color: #111827;
}

.student-profile__completion-list > div.is-complete i {
    background: #ecfdf5;
    color: #16a34a;
}

.student-profile__completion-list > div.is-complete small {
    color: #16a34a;
}

@media (max-width: 1199px) {
    .student-profile__container {
        grid-template-columns: 210px minmax(0, 1fr);
    }

    .student-profile__workspace {
        grid-template-columns: minmax(0, 1fr);
    }

    .student-profile__completion {
        position: static;
    }
}

@media (max-width: 991px) {
    .student-profile {
        padding: 24px 0 36px;
    }

    .student-profile__container,
    .student-profile__workspace {
        grid-template-columns: 1fr;
    }
}

@media (max-width: 767px) {
    .student-profile__topbar {
        flex-direction: column;
        align-items: flex-start;
    }

    .student-profile__photo-panel {
        flex-direction: column;
        align-items: flex-start;
    }

    .student-profile__fields,
    .student-profile__password-fields {
        grid-template-columns: 1fr;
    }

    .student-profile__panel-head {
        align-items: flex-start;
        flex-direction: column;
    }
}

@media (max-width: 575px) {
    .student-profile__container {
        padding: 0 10px;
    }

    .student-profile__topbar,
    .student-profile__panel {
        padding: 16px;
    }

    .student-profile__photo-copy strong {
        font-size: 20px;
    }

    .student-profile__save-inline,
    .student-profile__actions button {
        width: 100%;
    }
}
</style>
